import { connect } from 'react-redux';
import { keyBy } from 'lodash';
import { Tab, Tabs } from '@mui/material';
import i18next from 'i18next';
import React, { useEffect, useState } from 'react';

import { getClientInfo } from '@selectors/client';
import { getSalesPointStores } from '@selectors/stores';

// Commons
import { Button } from '@commons/utils/styledLibraryComponents';
import { GenericContentContainer } from '@commons/Layout/styledComponents';
import { getAuthorizedActions, getHasTransferDeliveryTime } from '@selectors/featureProps';
import { getFactorToConvertMetricToEntityUnit } from '@commons/utils/conversion';
import { getTheme } from '@commons/utils/theme';
import { TabPanel } from '@commons/TabPanel';
import {
  TRANSFER_DETAILS_VIEW,
  TRANSFER_STATUS,
} from 'src/routes/stocks/StocksInventories/Transfers/utils/constants.js';
import DisplayNumber from '@commons/DisplayNumber';
import Text, { ENUM_COLORS, ENUM_FONTS } from '@commons/Text';

import {
  Container,
  getTabStyles,
  HeaderContainer,
  HeaderWithActions,
  TabsContainer,
  TotalAndButtonContainer,
  TotalContainer,
  TotalWithDiff,
} from './styledComponents';

import { exportTransfer } from './utils/exportTransfer';
import { getActions } from './utils/getActions';
import { getClientStoreNameTranslation } from '@commons/utils/translations';
import RecipesDatagrid from './RecipesDatagrid';
import ReferenceDrawer from '../ReferenceDrawer';
import SupplierProductsDatagrid from './SupplierProductsDatagrid';

const TransferContent = (props) => {
  const {
    cancelInvoiced,
    currency,
    supplierProductsToDisplay,
    setSupplierProductsToDisplay,
    recipesToDisplay,
    setRecipesToDisplay,
    transferList,
    transferCostDate,
    transferDetailsView,
    setEnableDatePicker,
    setEnableTimePicker,
    client: { storeName },
    stores,
    hasTransferDeliveryTime,
    authorizedActions,
  } = props;

  const theme = getTheme();

  const [selectedTab, setSelectedTab] = useState(0);
  const [totalTransferNoteExclTaxes, setTotalTransferNoteExclTaxes] = useState(0);
  const [totalDeliveryNoteExclTaxes, setTotalDeliveryNoteExclTaxes] = useState(0);
  const [totalReceivedExclTaxes, setTotalReceivedExclTaxes] = useState(0);

  const [isReferenceDrawerOpen, setIsReferenceDrawerOpen] = useState(false);
  const [actions, setActions] = useState([]);
  const [newlyAddedReferenceId, setNewlyAddedReferenceId] = useState(null);

  const handleExport = () => {
    const translatedStoreName = getClientStoreNameTranslation(storeName, false);

    const transferedSupplierProducts = supplierProductsToDisplay.filter(({ packagings }) =>
      packagings.some(({ selectedQuantity }) => selectedQuantity > 0),
    );

    const transferedRecipes = recipesToDisplay.filter(
      ({ selectedQuantity }) => selectedQuantity > 0,
    );

    const storesKeyById = keyBy(stores, 'id');

    exportTransfer({
      transferList: {
        ...transferList,
        senderStoreName: storesKeyById[transferList.senderStoreId]?.name,
        recipientStoreName: storesKeyById[transferList.recipientStoreId]?.name,
      },
      transferedSupplierProducts,
      transferedRecipes,
      storeName: translatedStoreName,
      currency,
    });
  };

  useEffect(() => {
    setActions(
      getActions({
        transferDetailsView,
        setIsReferenceDrawerOpen,
        handleExport,
        setEnableDatePicker,
        setEnableTimePicker,
        hasTransferDeliveryTime,
        authorizedActions,
        transferList,
        stores,
        cancelInvoiced,
      }),
    );
  }, [transferDetailsView, supplierProductsToDisplay, recipesToDisplay, transferList]);

  useEffect(() => {
    const newSupplierProduct = supplierProductsToDisplay.find(({ isNewlyAdded }) => isNewlyAdded);
    if (newSupplierProduct) {
      setNewlyAddedReferenceId(newSupplierProduct.id);

      // Delete the highlight after 3 seconds
      setTimeout(() => {
        setSupplierProductsToDisplay(
          supplierProductsToDisplay.map((item) => ({ ...item, isNewlyAdded: false })),
        );
        setNewlyAddedReferenceId(null);
      }, 3000);
      return;
    }

    const newRecipe = recipesToDisplay.find(({ isNewlyAdded }) => isNewlyAdded);
    if (newRecipe) {
      setNewlyAddedReferenceId(newRecipe.id);

      // Delete the highlight after 3 seconds
      setTimeout(() => {
        setRecipesToDisplay(recipesToDisplay.map((item) => ({ ...item, isNewlyAdded: false })));
        setNewlyAddedReferenceId(null);
      }, 3000);
    }
  }, [supplierProductsToDisplay, recipesToDisplay]);

  useEffect(() => {
    setTotalTransferNoteExclTaxes(
      getTotalExclTaxes(['transferNoteQuantity'], supplierProductsToDisplay, recipesToDisplay),
    );
    setTotalDeliveryNoteExclTaxes(
      getTotalExclTaxes(
        ['deliveryNoteQuantity', 'transferNoteQuantity'],
        supplierProductsToDisplay,
        recipesToDisplay,
      ),
    );
    setTotalReceivedExclTaxes(
      getTotalExclTaxes(
        ['receivedQuantity', 'deliveryNoteQuantity', 'transferNoteQuantity'],
        supplierProductsToDisplay,
        recipesToDisplay,
      ),
    );
  }, [supplierProductsToDisplay, recipesToDisplay]);

  const getTotalExclTaxes = (quantityKeys, supplierProducts = [], recipes = []) => {
    const getFirstAvailableQuantity = (item) => {
      for (const key of quantityKeys) {
        if (item[key] != null) {
          return item[key];
        }
      }

      return 0;
    };

    const supplierTotal = supplierProducts.reduce((acc, { packagings }) => {
      packagings.forEach((packaging) => {
        const quantity = getFirstAvailableQuantity(packaging);
        const price = packaging.price || 0;

        acc += quantity * price;
      });

      return acc;
    }, 0);

    const recipeTotal = recipes.reduce((acc, recipe) => {
      const quantity = getFirstAvailableQuantity(recipe);
      const cost = recipe.cost || 0;
      const factor = getFactorToConvertMetricToEntityUnit(recipe.unit);

      return acc + quantity * cost * factor;
    }, 0);

    return supplierTotal + recipeTotal;
  };

  const handleTabChange = (event, tabValue) => {
    setSelectedTab(tabValue);
  };

  return (
    <Container>
      <HeaderContainer>
        <HeaderWithActions>
          <TabsContainer>
            <Tabs
              aria-label="transfer tabs"
              sx={{
                '& .MuiTabs-indicator': {
                  backgroundColor: theme.colors.brand.primary,
                  height: '3px',
                },
              }}
              value={selectedTab}
              onChange={handleTabChange}
            >
              <Tab
                label={i18next.t('GENERAL.SUPPLIER_PRODUCT_PLURAL')}
                sx={getTabStyles(theme, 1)}
              />
              <Tab label={i18next.t('GENERAL.RECIPE_PLURAL')} sx={getTabStyles(theme, 2)} />
            </Tabs>
          </TabsContainer>

          <TotalAndButtonContainer>
            {transferList.status >= TRANSFER_STATUS.DRAFT && !transferList.isSender && (
              <TotalContainer>
                <Text color={ENUM_COLORS.DGREY} font={ENUM_FONTS.TEXT_SMALL_HEIGHT_16}>
                  {i18next.t('STOCKS.TRANSFERS.TRANSFER_NOTE_TOTAL')}
                </Text>

                <Text font={ENUM_FONTS.TEXT_BIG_HEIGHT_16}>
                  <DisplayNumber
                    currency={currency}
                    number={totalTransferNoteExclTaxes}
                    displayCurrencyCode
                  />
                </Text>
              </TotalContainer>
            )}
            {((transferList.status === TRANSFER_STATUS.DRAFT && transferList.isSender) ||
              transferList.status >= TRANSFER_STATUS.REQUESTED) && (
              <TotalContainer>
                <Text color={ENUM_COLORS.DGREY} font={ENUM_FONTS.TEXT_SMALL_HEIGHT_16}>
                  {i18next.t('STOCKS.TRANSFERS.DELIVERY_NOTE_TOTAL')}
                </Text>

                {totalDeliveryNoteExclTaxes !== totalTransferNoteExclTaxes ? (
                  <TotalWithDiff>
                    <DisplayNumber
                      color={ENUM_COLORS.INFO_ORANGE}
                      currency={currency}
                      font={ENUM_FONTS.TEXT_BIG_HEIGHT_16}
                      number={totalDeliveryNoteExclTaxes}
                      displayCurrencyCode
                    />
                    <Text
                      color={ENUM_COLORS.INFO_ORANGE}
                      font={ENUM_FONTS.TEXT_BIG_HEIGHT_16}
                      style={{ marginLeft: '4px' }}
                    >
                      (
                    </Text>
                    <DisplayNumber
                      color={ENUM_COLORS.INFO_ORANGE}
                      currency={currency}
                      font={ENUM_FONTS.TEXT_BIG_HEIGHT_16}
                      number={totalDeliveryNoteExclTaxes - totalTransferNoteExclTaxes}
                    />
                    <Text color={ENUM_COLORS.INFO_ORANGE} font={ENUM_FONTS.TEXT_BIG_HEIGHT_16}>
                      )
                    </Text>
                  </TotalWithDiff>
                ) : (
                  <DisplayNumber
                    currency={currency}
                    font={ENUM_FONTS.TEXT_BIG_HEIGHT_16}
                    number={totalDeliveryNoteExclTaxes}
                    displayCurrencyCode
                  />
                )}
              </TotalContainer>
            )}

            {(transferDetailsView === TRANSFER_DETAILS_VIEW.RECEPTION ||
              transferList.status > TRANSFER_STATUS.SENT) && (
              <TotalContainer>
                <Text color={ENUM_COLORS.DGREY} font={ENUM_FONTS.TEXT_SMALL_HEIGHT_16}>
                  {i18next.t('INVOICE.INVOICE_CONTROLS.RECEIVED_TOTAL_EX_TAX')}
                </Text>

                {totalReceivedExclTaxes !== totalDeliveryNoteExclTaxes ? (
                  <TotalWithDiff>
                    <DisplayNumber
                      color={ENUM_COLORS.INFO_RED}
                      currency={currency}
                      font={ENUM_FONTS.TEXT_BIG_HEIGHT_16}
                      number={totalReceivedExclTaxes}
                      displayCurrencyCode
                    />
                    <Text
                      color={ENUM_COLORS.INFO_RED}
                      font={ENUM_FONTS.TEXT_BIG_HEIGHT_16}
                      style={{ marginLeft: '4px' }}
                    >
                      (
                    </Text>
                    <DisplayNumber
                      color={ENUM_COLORS.INFO_RED}
                      currency={currency}
                      font={ENUM_FONTS.TEXT_BIG_HEIGHT_16}
                      number={totalReceivedExclTaxes - totalDeliveryNoteExclTaxes}
                    />
                    <Text color={ENUM_COLORS.INFO_RED} font={ENUM_FONTS.TEXT_BIG_HEIGHT_16}>
                      )
                    </Text>
                  </TotalWithDiff>
                ) : (
                  <DisplayNumber
                    currency={currency}
                    font={ENUM_FONTS.TEXT_BIG_HEIGHT_16}
                    number={totalReceivedExclTaxes}
                    displayCurrencyCode
                  />
                )}
              </TotalContainer>
            )}

            {actions.length > 0 && (
              <Button
                actions={actions}
                color={'inpulse-outline'}
                iconDropdown={'/images/inpulse/ellipsis-black-small.svg'}
              />
            )}
          </TotalAndButtonContainer>
        </HeaderWithActions>
      </HeaderContainer>

      <GenericContentContainer padding={'0px 24px 16px 24px'}>
        <TabPanel index={0} value={selectedTab}>
          <SupplierProductsDatagrid
            newlyAddedReferenceId={newlyAddedReferenceId}
            setSupplierProductsToDisplay={setSupplierProductsToDisplay}
            supplierProductsToDisplay={supplierProductsToDisplay}
            transferDetailsView={transferDetailsView}
            transferList={transferList}
          />
        </TabPanel>

        <TabPanel index={1} value={selectedTab}>
          <RecipesDatagrid
            newlyAddedReferenceId={newlyAddedReferenceId}
            recipesToDisplay={recipesToDisplay}
            setRecipesToDisplay={setRecipesToDisplay}
            transferDetailsView={transferDetailsView}
            transferList={transferList}
          />
        </TabPanel>
      </GenericContentContainer>

      <ReferenceDrawer
        isOpen={isReferenceDrawerOpen}
        recipesToDisplay={recipesToDisplay}
        selectedTab={selectedTab}
        setRecipesToDisplay={setRecipesToDisplay}
        setSupplierProductsToDisplay={setSupplierProductsToDisplay}
        supplierProductsToDisplay={supplierProductsToDisplay}
        transferCostDate={transferCostDate}
        transferDetailsView={transferDetailsView}
        transferList={transferList}
        onClose={() => setIsReferenceDrawerOpen(false)}
      />
    </Container>
  );
};

const mapStateToProps = (state) => ({
  currency: state.baseReducer.currency,
  client: getClientInfo(state.baseReducer.user),
  stores: getSalesPointStores(state.baseReducer.activeStores),
  hasTransferDeliveryTime: getHasTransferDeliveryTime(state.baseReducer.userRights),
  authorizedActions: getAuthorizedActions(
    state.baseReducer.userRights,
    '/stocks/inventories/transfers',
  ),
});

export default connect(mapStateToProps)(TransferContent);
